/**
 * Tab Push Notifications nell'Admin Panel.
 * Gestione sottoscrizioni push e invio notifiche test.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { pushApi, PushSendRequest } from '../../api/push'
import { SkeletonList } from '../../components/Skeleton'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function PushTab() {
  const [showSendForm, setShowSendForm] = useState(false)
  const [sendForm, setSendForm] = useState<PushSendRequest>({
    user_id: '',
    title: '',
    body: '',
    url: '',
  })

  const queryClient = useQueryClient()

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['push-subscriptions'],
    queryFn: () => pushApi.listSubscriptions(),
    staleTime: 60 * 1000,
  })

  const sendMutation = useMutation({
    mutationFn: pushApi.send,
    onSuccess: () => {
      setSendForm({ user_id: '', title: '', body: '', url: '' })
      setShowSendForm(false)
      alert('Notifica inviata con successo')
    },
    onError: () => {
      alert('Errore durante l\'invio della notifica')
    },
  })

  const handleSend = () => {
    if (!sendForm.title || !sendForm.body) return
    sendMutation.mutate(sendForm)
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Notifiche Push</h2>
        <Button onClick={() => setShowSendForm(!showSendForm)}>
          + Invia Test
        </Button>
      </div>

      {/* Send Form */}
      {showSendForm && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <h3 className="text-md font-semibold text-foreground mb-4">Invia Notifica Test</h3>
            <div className="space-y-4">
              <div>
                <Label>User ID (opzionale)</Label>
                <Input
                  value={sendForm.user_id}
                  onChange={(e) => setSendForm({ ...sendForm, user_id: e.target.value })}
                  placeholder="Lascia vuoto per tutti"
                />
              </div>
              <div>
                <Label>Titolo</Label>
                <Input
                  value={sendForm.title}
                  onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                  placeholder="Titolo notifica"
                  required
                />
              </div>
              <div>
                <Label>Messaggio</Label>
                <Textarea
                  value={sendForm.body}
                  onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                  placeholder="Corpo della notifica"
                  rows={3}
                  required
                />
              </div>
              <div>
                <Label>URL destinazione (opzionale)</Label>
                <Input
                  value={sendForm.url}
                  onChange={(e) => setSendForm({ ...sendForm, url: e.target.value })}
                  placeholder="/emails/123"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSend} disabled={sendMutation.isPending}>
                  {sendMutation.isPending ? 'Invio...' : 'Invia'}
                </Button>
                <Button variant="outline" onClick={() => setShowSendForm(false)}>
                  Annulla
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscriptions List */}
      {isLoading ? (
        <SkeletonList count={3} />
      ) : subscriptions?.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            Nessuna sottoscrizione push registrata
          </CardContent>
        </Card>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>User Agent</TableHead>
                <TableHead>Registrato il</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscriptions?.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium">
                    {sub.username}
                    <span className="text-muted-foreground text-xs ml-1">({sub.user_id})</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs truncate max-w-xs">
                    {sub.endpoint}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">
                    {sub.user_agent}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {new Date(sub.created_at).toLocaleDateString('it-IT', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
